/**
 * Unified message models for backend and frontend
 * Simplified structure with metadata consolidation
 */

import type { AnswerCardState } from '@/types/answerCard.types';
import type { ToolCallArtifact } from '@/types/toolViews.types';

export interface ThreadMessage {
  id: string;
  threadId: string;
  role: 'user' | 'assistant' | 'system';
  content: MessageContent[];
  status: MessageStatus;
  createdAt: Date;
  updatedAt: Date;
  metadata: MessageMetadata;
  data?: any; // Optional data field for custom event data
}

export interface MessageContent {
  type: ContentType;
  id: string;
  order?: number;
  timestamp?: Date;
}

export interface TextContent extends MessageContent {
  type: 'text';
  text: string;
}

export interface ToolCallContent extends MessageContent {
  type: 'tool-call';
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  result?: unknown;
  isError?: boolean;
  status: 'pending' | 'executing' | 'complete' | 'error';
  /** The tool's human title, known when the call starts; absent on old blocks. */
  title?: string;
  /**
   * What the UI draws for the call, from its call or its result; a result
   * without one keeps the call's. Null or absent when the reasoner sent none.
   */
  artifact?: ToolCallArtifact | null;
}

export interface ThinkingContent extends MessageContent {
  type: 'thinking';
  content: string;
  isComplete: boolean;
}

export interface SubSessionContent extends MessageContent {
  type: 'sub-session';
  sessionId: string;
  agentName: string;
  content: MessageContent[];
  isCollapsed?: boolean;
  status: MessageStatus;
  /** Who was asked: "Asked the analyst"; live, the title of its tool call. */
  title?: string;
  /** The task the sub-agent was given; saved with the session. */
  summary?: string | null;
  metadata?: MessageMetadata;
}

export type ContentType = 'text' | 'tool-call' | 'thinking' | 'sub-session';
export type MessageStatus = 'pending' | 'streaming' | 'complete' | 'error' | 'cancelled';
// Values sent by the backend in the FINISHED event's finish_reason field.
// max_tokens (Anthropic/OpenAI) and length (other providers) both indicate
// the output was cut off by the token limit and intentionally share a label.
export type FinishReason =
  | 'max_tokens'
  | 'length'
  | 'content_filter'
  | 'stop_sequence'
  | 'unknown';

export interface TokenUsageStats {
  totalTokens: number;
  maxTokens: number;
  percentage: number;
  tokensRemaining: number;
  modelCalls: number;
}

/**
 * Per-message subgraph payload attached by review_reasoner at FINISHED.
 *
 * Mirrors the api-gateway `mode=graph` shape so the existing UI
 * formatter (`formatNodesAndRelationshipsFromResult`) consumes it
 * unchanged. Replaces the previous per-render `INITIAL_CYPHER_QUERY`
 * round-trip — long histories no longer drag Neo4j on every scroll.
 */
export interface SubgraphRelEndpoint {
  id?: number | null;
  element_id?: string | null;
}

export interface SubgraphRawNode {
  id?: number | null;
  element_id?: string | null;
  labels: string[];
  properties: Record<string, unknown>;
}

export interface SubgraphRawRelationship {
  id?: number | null;
  element_id?: string | null;
  type: string;
  properties: Record<string, unknown>;
  start_node?: SubgraphRelEndpoint | null;
  end_node?: SubgraphRelEndpoint | null;
}

export interface SubgraphData {
  nodes: SubgraphRawNode[];
  relationships: SubgraphRawRelationship[];
  focus_ids: string[];
  limit_reached?: boolean;
}

export interface MessageMetadata {
  sequenceNumber?: number;
  
  agentName?: string;
  agentType?: 'orchestrator' | 'sub_agent';

  // Hierarchical relationships
  sessionId?: string;
  parentMessageId?: string;
  
  // Data references (for graph nodes)
  availableIds?: string[];

  subgraph?: SubgraphData;

  // Set by FINISHED when a card is coming; the reasoner writes the final state
  // into the saved message after the stream has closed.
  answerCard?: AnswerCardState;

  // Event tracking
  eventType?: string;
  
  // Tool tracking
  toolCallId?: string;
  
  // Token usage stats
  tokenUsage?: TokenUsageStats;

  // Reason the LLM stopped. "stop" is the normal path; other values
  // are represented by FinishReason and shown as warning banners.
  finishReason?: FinishReason | 'stop';

  // Exact token counts reported by the LLM in the terminal completion event.
  // Authoritative source: on_chat_model_end response_metadata / usage_metadata.
  // Not overwritten by approximate running-total DATA events.
  llmTokens?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };

  // Custom fields for extensibility
  custom?: Record<string, unknown>;

  // Error message persisted when the stream fails (from meta.error in Postgres)
  error?: string;

  // Arbitrary pass-through keys from the backend meta object
  [key: string]: unknown;
}

// LLMEvent integration types (unchanged)
export interface LLMEvent {
  id: string;
  type: string;
  result?: string | null;
  metadata?: Record<string, any> | null;
  tool_name?: string | null;
  tool_params?: string | null;
  tool_call_id?: string | null;
  is_error?: boolean | null;
  data?: any;
  /** A TOOL_CALL's title. */
  title?: string | null;
  /** The tool's view, on a TOOL_CALL or a TOOL_RESULT. */
  artifact?: ToolCallArtifact | null;
  error?: any;
  operations?: any;
}

export const EventType = {
  TOOL_CALL: "tool_call",
  TOOL_CALL_START: "tool_call_start",
  TOOL_START: "tool_start",
  TOOL_END: "tool_end",
  TOOL_RESULT: "tool_result",
  AI_MESSAGE: "ai_message",
  AI_MESSAGE_CHUNK: "ai_message_chunk",
  THINKING: "thinking",
  THINKING_CHUNK: "thinking_chunk",
  TOOL_CALL_CHUNK: "tool_call_chunk",
  ERROR: "error",
  DATA: "data",
  SUMMARY: "summary",
  FINISHED: "finished",
  TURN_FINISH: "turn_finish",
  PLAN: "plan",
} as const;

export type EventType = typeof EventType[keyof typeof EventType];

// User message types for sending
export interface UserMessagePart {
  readonly type: 'text' | 'image' | 'file';
  readonly text?: string;
  readonly url?: string;
  readonly mimeType?: string;
}

export interface UserMessage {
  readonly role: 'user';
  readonly content: readonly UserMessagePart[];
}